import { useState, useEffect, memo } from 'react'
import { parse } from 'querystringify'

// Components
import { Heading } from '@tracklib/kit'
import TrackList from 'components/browse/tracklist'

// Hooks
import { useSearchQuery } from 'modules/browse/hooks'
import { useCollection } from 'modules/collections/hooks'

// Types
import { ApiRequest, QueryObject, Loop, DisplayMode } from 'types'

// Utils
import { playlistNameFromQueryString } from 'utils'

import styles from './querytracklist.css'

function shuffleArray<T>(d: Array<T>): Array<T> {
  return d
    .map(a => ({ sort: Math.random(), value: a }))
    .sort((a, b) => a.sort - b.sort)
    .map(a => a.value)
}

type QueryTrackListProps = {
  query: string
  mode?: DisplayMode
  heading: string
  subheading: string
  showMoreText: string
  showMoreUrl: string
  datatesting?: string
  twoColumns?: boolean
  maxShown?: number
  maxSize?: number
  randomOrder?: boolean
  onClick?: () => void
}

const QueryTrackList = ({
  query,
  mode,
  heading,
  subheading,
  showMoreText,
  showMoreUrl,
  datatesting,
  twoColumns,
  maxShown,
  maxSize,
  randomOrder,
  onClick,
}: QueryTrackListProps): JSX.Element => {
  const searchQuery = parse(query) as QueryObject
  const { trackIds, request } = useSearchQuery(searchQuery, undefined, true)
  const [tracks, setTracks] = useState<Array<{ id: number; loop?: Loop }>>(
    trackIds ? trackIds.filter((track, i) => (maxSize ? i < maxSize : true)) : [],
  )

  const { collection, request: collectionRequest } = useCollection(searchQuery.collection ? +searchQuery.collection : undefined)
  const collectionTracks = collection?.tracks
  useEffect(() => {
    if (collectionTracks?.length) {
      const newTracks = collectionTracks
        .filter(track => trackIds.some(t => t.id === track.id))
        .filter((_, i) => (maxSize ? i < maxSize : true))

      setTracks(randomOrder ? shuffleArray(newTracks) : newTracks)
    } else if (!searchQuery.collection) {
      const newTracks = trackIds.filter((track, i) => (maxSize ? i < maxSize : true))
      setTracks(randomOrder ? shuffleArray(newTracks) : newTracks)
    }
  }, [JSON.stringify(trackIds), JSON.stringify(collectionTracks), randomOrder]) // eslint-disable-line
  const playlistName = playlistNameFromQueryString(query)
  const half = (value: number) => Math.floor(value / 2)
  const lists = [tracks.slice(0, half(tracks.length)), tracks.slice(half(tracks.length), tracks.length)]

  // NOTE(Kitti): Requests needs to be tempered with because setting state (setTracks) takes 1 render
  const collectionTracksNotSet = !!collectionTracks?.length && !lists[0]?.length
  const collectionAndQueryRequest: ApiRequest = {
    isLoading: request.isLoading || collectionRequest.isLoading || collectionTracksNotSet,
    isDone: request.isDone && collectionRequest.isDone && !collectionTracksNotSet,
    hasError: request.hasError || collectionRequest.hasError,
  }
  const tracksNotSet = !!trackIds.length && !tracks?.length
  const regularRequest = {
    isLoading: request.isLoading || tracksNotSet,
    isDone: request.isDone && !tracksNotSet,
    hasError: request.hasError,
  }

  /* eslint-disable */
  return (
    <div className={styles.list} datatesting={datatesting} onClick={onClick}>
      {/* eslint-enable */}
      {heading && <Heading heading={heading} subheading={subheading} linkText={showMoreText} linkUrl={showMoreUrl} />}
      {twoColumns ? (
        <div className={styles.lists}>
          {lists.map((list, index) => (
            <div key={String(index)} className={styles.columnList}>
              <TrackList
                trackIds={list}
                request={searchQuery.collection ? collectionAndQueryRequest : regularRequest}
                recommendationsRequest={request}
                totalTrackCount={tracks.length}
                contextKey={playlistName}
                mode="extraCompact"
                hideRecommendations
                maxCount={maxShown ? half(maxShown) : searchQuery.size}
              />
            </div>
          ))}
        </div>
      ) : (
        <TrackList
          trackIds={tracks}
          contextKey={playlistName}
          mode={mode}
          maxCount={maxShown || searchQuery.size}
          hideRecommendations
          request={regularRequest}
        />
      )}
    </div>
  )
}

export default memo<QueryTrackListProps>(QueryTrackList)
